import {
  parseEffect as protocolParseEffect,
  type ParseRequest,
} from '@okrapdf/protocol/http-parse-client';
import { compileComponent, type ComponentNode } from '@okrapdf/component';
import { Clock, Data, Duration, Effect, Exit, Option, Schedule } from 'effect';
import { normalizeDocumentAssets } from './assets';
import { isStructuredOutputErrorCode, OkraRuntimeError, StructuredOutputError } from './errors';
import { DocumentEventStream, type DocumentEventStreamOptions } from './events';
import { normalizeStructuredSchema, normalizeStructuredSchemaEffect } from './structured-schema';
import { createClientEventStream } from './client-event-stream';
import {
  contextEffectClientToPromise,
  createHttpContextEffectClient,
  type OkraContextClient,
  type OkraContextEffectClient,
} from './context';

const uploadProgressStreams = new WeakMap<OkraSession, DocumentEventStream>();

const clientHttpErrorCauses = new WeakMap<ClientHttpError, unknown>();

export class ClientHttpError extends Data.TaggedError('ClientHttpError')<{
  readonly method: string;
  readonly status?: number;
}> {
  constructor(input: { method: string; status?: number; cause: unknown }) {
    super({
      method: input.method,
      ...(input.status === undefined ? {} : { status: input.status }),
    });
    clientHttpErrorCauses.set(this, input.cause);
  }
  get cause(): unknown {
    return clientHttpErrorCauses.get(this);
  }
}

function withClientResponseReaderEffect<A>(
  response: Response,
  use: (reader: ReadableStreamDefaultReader<Uint8Array>) => Effect.Effect<A, ClientHttpError>,
): Effect.Effect<A, ClientHttpError> {
  const failure = (cause: unknown) => new ClientHttpError({ method: 'read_response', cause });
  return Effect.acquireUseRelease(
    Effect.try({ try: () => response.body!.getReader(), catch: failure }),
    use,
    (reader) =>
      Effect.tryPromise({ try: () => reader.cancel(), catch: failure }).pipe(
        Effect.ignore,
        Effect.ensuring(Effect.sync(() => reader.releaseLock())),
      ),
  );
}

/** Own response consumption without changing the injected fetch(url, init) contract. */
export function readClientResponseTextEffect(
  response: Response,
): Effect.Effect<string, ClientHttpError> {
  if (!response.body) return Effect.succeed('');
  const failure = (cause: unknown) => new ClientHttpError({ method: 'read_text', cause });
  return withClientResponseReaderEffect(response, (reader) =>
    Effect.gen(function* () {
      const decoder = new TextDecoder();
      let text = '';
      while (true) {
        const chunk = yield* Effect.tryPromise({ try: () => reader.read(), catch: failure });
        if (chunk.done) return text + decoder.decode();
        text += decoder.decode(chunk.value, { stream: true });
      }
    }),
  );
}

const readClientResponseBytesEffect = Effect.fn('sdk.readResponseBytes')(function* (
  response: Response,
) {
  if (!response.body) return new Uint8Array();
  return yield* withClientResponseReaderEffect(response, (reader) =>
    Effect.gen(function* () {
      const chunks: Uint8Array[] = [];
      let length = 0;
      while (true) {
        const chunk = yield* Effect.tryPromise({
          try: () => reader.read(),
          catch: (cause) => new ClientHttpError({ method: 'read_bytes', cause }),
        });
        if (chunk.done) break;
        chunks.push(chunk.value);
        length += chunk.value.length;
      }
      const result = new Uint8Array(length);
      let offset = 0;
      for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.length;
      }
      return result;
    }),
  );
});

const readClientResponseLinesEffect = Effect.fn('sdk.readResponseLines')(function* (
  response: Response,
  consume: (line: string) => Effect.Effect<void, ClientHttpError>,
  flushFinal: boolean,
) {
  yield* withClientResponseReaderEffect(response, (reader) =>
    Effect.gen(function* () {
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const chunk = yield* Effect.tryPromise({
          try: () => reader.read(),
          catch: (cause) => new ClientHttpError({ method: 'read_lines', cause }),
        });
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) yield* consume(line);
      }
      if (flushFinal) yield* consume(buffer + decoder.decode());
    }),
  );
});
import { prepareDynamicWorkflowEffect, restoreDynamicWorkflowError } from './workflows';
import type {
  DocumentAsset,
  ApiAction,
  ApiResource,
  ApiResourceCatalog,
  OkraClientOptions,
  OkraFile,
  JobListOptions,
  JobListResponse,
  OkraJob,
  ParseOptions,
  ParseJob,
  Collection,
  CollectionExportFormat,
  CollectionExportOptions,
  CollectionExportEvent,
  CollectionMarkdownExport,
  CollectionQueryEvent,
  CollectionQueryOptions,
  CollectionQueryResult,
  CollectionQueryStream,
  CollectionSummary,
  CompletionEvent,
  CompletionOptions,
  CreateJobOptions,
  DeleteFileResult,
  DeleteDocumentResult,
  DocumentAnswer,
  DocumentConfigResult,
  DocumentConfigUpdate,
  DocumentListItem,
  DocumentListResponse,
  DocumentMarkdownExport,
  DocumentStatus,
  EntitiesResponse,
  FileListOptions,
  FileUploadOptions,
  GenerateOptions,
  GenerateResult,
  InvoiceExtractionException,
  InvoiceExtractionExceptionList,
  InvoiceExtractionExceptionResolution,
  InvoiceExtractionResolveExceptionOptions,
  InvoiceExtractionRun,
  InvoiceExtractionRunList,
  InvoiceExtractionRunListOptions,
  InvoiceExtractionRunOptions,
  LogEntry,
  LogsOptions,
  MarkdownPage,
  OkraFiles,
  OkraFileListResponse,
  Page,
  PublishResult,
  QueryResult,
  ReadDocumentOptions,
  ReadDocumentResult,
  ReparseOptions,
  ReparseResult,
  RuntimeErrorCode,
  ShareLinkOptions,
  ShareLinkResult,
  SessionAttachOptions,
  SessionCreateOptions,
  DocumentSpec,
  DocumentSpecRecord,
  SessionState,
  OkraSession,
  StructuredOutputErrorCode,
  StructuredOutputMeta,
  StructuredSchema,
  ApiKeyWorkflowConfigResponse,
  ApplyWorkflowOptions,
  ApplyWorkflowResult,
  UploadInput,
  UploadOptions,
  VerifyParams,
  VerifyResult,
  WaitOptions,
} from './types';
import type {
  DynamicWorkflowDatasetRef,
  DynamicWorkflowDefinition,
  DynamicWorkflowRun,
  DynamicWorkflowRunList,
  DynamicWorkflowRunListOptions,
  DynamicWorkflowRunOptions,
} from './workflows';

const DEFAULT_BASE_URL = 'https://api.okrapdf.com';
const DEFAULT_WAIT_TIMEOUT_MS = 5 * 60_000;
const DEFAULT_WAIT_POLL_MS = 1_500;
const DEFAULT_FILE_DIRECT_UPLOAD_THRESHOLD_BYTES = 16 * 1024 * 1024;
const COMPLETE_PHASES = new Set(['complete', 'awaiting_review']);
const TERMINAL_ERROR_PHASES = new Set(['error']);
const NODE_FS_PROMISES_SPECIFIER = `node:${'fs/promises'}`;
const NODE_PATH_SPECIFIER = `node:${'path'}`;

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function isDocumentId(value: string): boolean {
  return /^(?:ocr|doc)-[A-Za-z0-9_-]+$/.test(value);
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '');
}

function makeDocId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `doc-${crypto.randomUUID().replace(/-/g, '').slice(0, 20)}`;
  }
  const rand = Math.random().toString(36).slice(2, 22);
  return `doc-${rand}`;
}

function normalizeInvoiceWorkflowInputs(
  inputs: InvoiceExtractionRunOptions['inputs'],
): Array<{ file_id: string }> {
  if (!Array.isArray(inputs) || inputs.length === 0) {
    throw new OkraRuntimeError(
      'INVALID_REQUEST',
      'invoice extraction requires at least one file input',
      400,
    );
  }

  return inputs.map((input) => {
    const fileId = typeof input === 'string' ? input : (input.file_id ?? input.fileId ?? '');
    if (!fileId.trim()) {
      throw new OkraRuntimeError(
        'INVALID_REQUEST',
        'invoice extraction inputs must include a file id',
        400,
      );
    }
    return { file_id: fileId };
  });
}

type InvoiceExtractionRunApiResponse = {
  id: string;
  run_id: string;
  status: string;
  workflow_name: 'invoice-extraction';
  workflow_label: string;
  workflow_version: number;
  quality: InvoiceExtractionRun['quality'];
  table_id: string;
  agent_id?: string | null;
  inputs: Array<{ file_id: string }>;
  // Compat URL fields are absent on the deprecated POST response (#1389);
  // session-backed GET responses still include them.
  events_url?: string;
  stream_url?: string;
  rows_url?: string;
  exports?: {
    csv_url: string;
    json_url: string;
    xml_url?: string;
    xlsx_url?: string;
    [key: string]: unknown;
  };
  created_at: string;
};

type InvoiceExtractionExceptionApiResponse = {
  id: string;
  run_id: string;
  table_id: string;
  row_id?: string | null;
  file_id?: string | null;
  field?: string | null;
  code: InvoiceExtractionException['code'];
  severity: InvoiceExtractionException['severity'];
  status: InvoiceExtractionException['status'];
  message: string;
  value?: unknown;
  source_page?: number | null;
  source_bbox?: string | null;
  created_at: string;
};

type WorkflowResourceRunResponse = {
  object?: string;
  id?: string;
  run_id?: string;
  workflow_id?: string;
  workflow_version_id?: string;
  status?: string;
  status_url?: string;
  controller_output?: unknown;
  output?: unknown;
  stats?: unknown;
  error?: unknown;
  created_at?: string;
  updated_at?: string;
  manifest?: {
    created_at?: string;
    updated_at?: string;
  };
};

type WorkflowResourceCreateResponse = {
  object?: string;
  id: string;
  workflow_id?: string;
  name?: string | null;
  run?: WorkflowResourceRunResponse | null;
  created_at?: string;
};

type WorkflowRunListResourceResponse = {
  object?: 'list';
  data?: WorkflowResourceRunResponse[];
  has_more?: boolean;
  next_cursor?: string | null;
};

function normalizeDynamicWorkflowStatus(status: unknown): DynamicWorkflowRun['status'] {
  switch (typeof status === 'string' ? status : '') {
    case 'planned':
      return 'planned';
    case 'queued':
      return 'queued';
    case 'running':
      return 'running';
    case 'completed':
    case 'complete':
    case 'done':
    case 'succeeded':
      return 'succeeded';
    case 'failed':
    case 'error':
      return 'failed';
    case 'cancelled':
    case 'canceled':
      return 'canceled';
    default:
      return 'queued';
  }
}

function mapWorkflowResourceRun(
  run: WorkflowResourceRunResponse,
  fallbackWorkflowId?: string,
  dataset?: DynamicWorkflowDatasetRef,
): DynamicWorkflowRun {
  const runId = run.run_id ?? run.id ?? '';
  const workflowId = run.workflow_id ?? fallbackWorkflowId ?? '';
  return {
    id: runId,
    object: 'workflow_eval_run',
    status: normalizeDynamicWorkflowStatus(run.status),
    workflowId,
    ...(dataset ? { dataset } : {}),
    ...(run.status_url ? { eventsUrl: run.status_url, resultsUrl: run.status_url } : {}),
    summary: {
      controller_output: run.controller_output ?? null,
      output: run.output ?? null,
      stats: run.stats ?? null,
      error: run.error ?? null,
    },
    createdAt: run.created_at ?? run.manifest?.created_at,
    updatedAt: run.updated_at ?? run.manifest?.updated_at,
  };
}

function mapInvoiceExtractionRun(result: InvoiceExtractionRunApiResponse): InvoiceExtractionRun {
  return {
    id: result.id,
    runId: result.run_id,
    status: result.status,
    workflowName: result.workflow_name,
    workflowLabel: result.workflow_label,
    workflowVersion: result.workflow_version,
    quality: result.quality,
    tableId: result.table_id,
    agentId: result.agent_id,
    inputs: result.inputs.map((input) => ({ fileId: input.file_id })),
    ...(result.events_url ? { eventsUrl: result.events_url } : {}),
    ...(result.stream_url ? { streamUrl: result.stream_url } : {}),
    ...(result.rows_url ? { rowsUrl: result.rows_url } : {}),
    ...(result.exports
      ? {
          exports: {
            csvUrl: result.exports.csv_url,
            jsonUrl: result.exports.json_url,
            ...(result.exports.xml_url ? { xmlUrl: result.exports.xml_url } : {}),
            ...(result.exports.xlsx_url ? { xlsxUrl: result.exports.xlsx_url } : {}),
            ...result.exports,
          },
        }
      : {}),
    createdAt: result.created_at,
  };
}

function mapInvoiceExtractionException(
  result: InvoiceExtractionExceptionApiResponse,
): InvoiceExtractionException {
  return {
    id: result.id,
    runId: result.run_id,
    tableId: result.table_id,
    rowId: result.row_id,
    fileId: result.file_id,
    field: result.field,
    code: result.code,
    severity: result.severity,
    status: result.status,
    message: result.message,
    value: result.value,
    sourcePage: result.source_page,
    sourceBbox: result.source_bbox,
    createdAt: result.created_at,
  };
}

function normalizeDocumentStatus(status: DocumentStatus): DocumentStatus {
  const totalPages =
    typeof status.pagesTotal === 'number'
      ? status.pagesTotal
      : typeof status.totalPages === 'number'
        ? status.totalPages
        : undefined;

  if (typeof totalPages !== 'number' || totalPages === status.pagesTotal) {
    return status;
  }

  return {
    ...status,
    pagesTotal: totalPages,
  };
}

function mergeCapabilitiesIntoDocumentSpec(
  spec: DocumentSpec,
  capabilities: ApplyWorkflowOptions['capabilities'],
): DocumentSpec {
  const next: DocumentSpec = {
    ...spec,
    extract: { ...spec.extract },
    features: { ...spec.features },
  };

  if (typeof capabilities.vlm_qwen === 'boolean') {
    next.features.vlm_qwen = capabilities.vlm_qwen;
  }
  if (typeof capabilities.structural_check === 'boolean') {
    next.features.structural_check = capabilities.structural_check;
  }
  if (typeof capabilities.sandbox_verify === 'boolean') {
    next.features.sandbox_verify = capabilities.sandbox_verify;
  }
  if (typeof capabilities.search === 'boolean') {
    next.features.search = capabilities.search;
  }

  const ocrPhase = capabilities.phases?.ocr;
  if (ocrPhase?.vendor) {
    next.extract.provider = ocrPhase.vendor;
  } else if (ocrPhase?.enabled === false) {
    next.extract.provider = null;
  }

  return next;
}

function toUint8Array(input: ArrayBuffer | Uint8Array): Uint8Array {
  if (input instanceof Uint8Array) return input;
  return new Uint8Array(input);
}

interface NodeFsModule {
  readFile(path: string, options?: { signal?: AbortSignal }): Promise<ArrayBuffer | Uint8Array>;
}

interface NodePathModule {
  basename(path: string): string;
}

interface NamedBlob extends Blob {
  name?: string;
}

function isBlobLike(input: unknown): input is Blob {
  if (typeof Blob !== 'undefined' && input instanceof Blob) return true;
  return (
    !!input &&
    typeof input === 'object' &&
    typeof (input as { arrayBuffer?: unknown }).arrayBuffer === 'function'
  );
}

function inferBlobName(input: Blob, fallback: string): string {
  const named = input as NamedBlob;
  if (typeof named.name === 'string' && named.name.trim() !== '') {
    return named.name;
  }
  return fallback;
}

function toHeaderSafeFileName(fileName: string): string {
  const leaf = fileName.split(/[\\/]/).pop() || fileName;
  const cleaned = leaf.replace(/[\r\n]/g, ' ').trim();

  if (/^[\x20-\x7E]+$/.test(cleaned)) {
    return cleaned;
  }

  const extMatch = cleaned.match(/(\.[A-Za-z0-9]{1,10})$/);
  const extension = extMatch?.[1].toLowerCase() ?? '';
  const base = extension ? cleaned.slice(0, -extension.length) : cleaned;

  const asciiBase = base
    .normalize('NFKD')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/[^A-Za-z0-9._ -]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[._-]+|[._-]+$/g, '');

  return `${asciiBase || 'document'}${extension || '.pdf'}`;
}

function normalizePdfFileName(fileName: string): string {
  const safe = toHeaderSafeFileName(fileName || 'document.pdf');
  if (safe.toLowerCase().endsWith('.pdf')) {
    return safe;
  }
  const stripped = safe.replace(/\.[A-Za-z0-9]{1,10}$/u, '');
  return `${stripped || 'document'}.pdf`;
}

function bytesToBase64(bytes: Uint8Array): string {
  // Build a binary string in chunks to avoid `String.fromCharCode(...bytes)`
  // exceeding the JS argument-list limit on large PDFs.
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  if (typeof btoa === 'function') return btoa(binary);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bufferCtor = (globalThis as any).Buffer;
  if (bufferCtor) return bufferCtor.from(bytes).toString('base64');
  throw new Error('No base64 encoder available in this runtime');
}

function uint8ArrayToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

const sha256HexFromBytesEffect = Effect.fn('sdk.fileDigest')(function* (bytes: Uint8Array) {
  const digest = yield* Effect.tryPromise({
    try: () => crypto.subtle.digest('SHA-256', uint8ArrayToArrayBuffer(bytes)),
    catch: (cause) => new ClientHttpError({ method: 'file_digest', cause }),
  });
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
});

function projectLocalFileError(inputPath: string, error: unknown): OkraRuntimeError {
  const code =
    typeof error === 'object' && error && 'code' in error
      ? String((error as { code?: unknown }).code)
      : undefined;
  const msg = error instanceof Error ? error.message : String(error);

  if (code === 'ENOENT') {
    const message = `Local file not found: ${inputPath}`;
    return new OkraRuntimeError('INVALID_REQUEST', message, 400, {
      error: 'file_not_found',
      message,
      hint: 'Check the file path and run the command again.',
      retryable: false,
      path: inputPath,
      next_actions: [{ cmd: 'okra upload ./document.pdf', why: 'Pass a readable local PDF path.' }],
    });
  }

  if (code === 'EACCES' || code === 'EPERM') {
    const message = `Cannot read local file (${code}): ${inputPath}`;
    return new OkraRuntimeError('INVALID_REQUEST', message, 400, {
      error: 'file_unreadable',
      message,
      hint: 'Fix the file permissions or choose a readable copy.',
      retryable: false,
      path: inputPath,
      next_actions: [{ cmd: 'ls -l <file>', why: 'Inspect the file permissions before retrying.' }],
    });
  }

  if (code === 'ERR_MODULE_NOT_FOUND' || code === 'ERR_UNKNOWN_BUILTIN_MODULE') {
    return new OkraRuntimeError(
      'INVALID_REQUEST',
      'Local file path uploads are only supported in Node.js. In browser runtimes, pass File/Blob, ArrayBuffer, Uint8Array, or URL.',
      400,
      error,
    );
  }

  return new OkraRuntimeError(
    'INVALID_REQUEST',
    `Failed to read local file "${inputPath}": ${msg}`,
    400,
    error,
  );
}

const readLocalFileEffect = Effect.fn('sdk.readLocalFile')(function* (inputPath: string) {
  const failure = (cause: unknown) =>
    new ClientHttpError({
      method: 'read_local_file',
      cause: projectLocalFileError(inputPath, cause),
    });
  const [fsModule, pathModule] = yield* Effect.all(
    [
      Effect.tryPromise({
        try: () => import(/* @vite-ignore */ NODE_FS_PROMISES_SPECIFIER) as Promise<NodeFsModule>,
        catch: failure,
      }),
      Effect.tryPromise({
        try: () => import(/* @vite-ignore */ NODE_PATH_SPECIFIER) as Promise<NodePathModule>,
        catch: failure,
      }),
    ],
    { concurrency: 'unbounded' },
  );
  const raw = yield* Effect.tryPromise({
    try: (signal) => fsModule.readFile(inputPath, { signal }),
    catch: failure,
  });
  return yield* Effect.try({
    try: () => ({ bytes: toUint8Array(raw), fileName: pathModule.basename(inputPath) }),
    catch: failure,
  });
});

interface StructuredErrorEnvelope {
  code?: string;
  message?: string;
  details?: unknown;
  error?: string | { message?: string; code?: string; details?: unknown; type?: string };
}

interface OkraFileUploadSession {
  id: string;
  file_id: string;
  object: 'file_upload';
  file_name: string;
  r2_key: string;
  upload_url: string;
  upload_method: 'PUT';
  upload_headers: Record<string, string>;
  finalize_url: string;
  expires_in: number;
  max_bytes: number;
  workflow_bound: false;
}

function isStructuredCode(code: string | undefined): code is StructuredOutputErrorCode {
  return isStructuredOutputErrorCode(code);
}

function getErrorMessage(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim() !== '') return value;
  return undefined;
}

// ─── Session Handle ──────────────────────────────────────────────────────────

class OkraSessionHandle implements OkraSession {
  readonly id: string;
  readonly modelEndpoint: string;
  #model?: string;
  #client: OkraClient;

  constructor(client: OkraClient, documentId: string, model?: string) {
    this.#client = client;
    this.id = documentId;
    this.modelEndpoint = client.modelEndpoint(documentId);
    this.#model = model;
  }

  get model(): string | undefined {
    return this.#model;
  }

  cacheOwner(): object {
    return this.#client;
  }

  state(): SessionState {
    return {
      id: this.id,
      model: this.#model,
      modelEndpoint: this.modelEndpoint,
    };
  }

  async setModel(model: string): Promise<void> {
    const normalized = model.trim();
    if (!normalized) {
      throw new OkraRuntimeError(
        'INVALID_REQUEST',
        'session.setModel requires a non-empty model',
        400,
      );
    }
    this.#model = normalized;
  }

  status(signal?: AbortSignal): Promise<DocumentStatus> {
    return this.#client.status(this.id, signal);
  }

  statusEffect(signal?: AbortSignal) {
    return this.#client.statusEffect(this.id, signal);
  }

  wait(options?: WaitOptions): Promise<DocumentStatus> {
    return this.#client.wait(this.id, options);
  }

  pages(options?: { range?: string; signal?: AbortSignal }): Promise<Page[]> {
    return this.#client.pages(this.id, options);
  }

  pagesEffect(signal?: AbortSignal) {
    return this.#client.pagesEffect(this.id, { signal });
  }

  page(pageNumber: number, signal?: AbortSignal): Promise<Page> {
    return this.#client.page(this.id, pageNumber, signal);
  }

  pageEffect(pageNumber: number, signal?: AbortSignal) {
    return this.#client.pageEffect(this.id, pageNumber, signal);
  }

  entities(options?: {
    type?: string;
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  }): Promise<EntitiesResponse> {
    return this.#client.entities(this.id, options);
  }

  downloadUrl(): string {
    return this.#client.downloadUrl(this.id);
  }

  query(sql: string, signal?: AbortSignal): Promise<QueryResult> {
    return this.#client.query(this.id, sql, signal);
  }

  logs(options?: LogsOptions): Promise<LogEntry[]> {
    return this.#client.logs(this.id, options);
  }

  publish(signal?: AbortSignal): Promise<PublishResult> {
    return this.#client.publish(this.id, signal);
  }

  shareLink(options?: ShareLinkOptions): Promise<ShareLinkResult> {
    return this.#client.shareLink(this.id, options);
  }

  assets(signal?: AbortSignal): Promise<DocumentAsset[]> {
    return this.#client.assets(this.id, signal);
  }

  asset<T = Record<string, unknown>>(
    assetId: string,
    signal?: AbortSignal,
  ): Promise<DocumentAsset<T> | null> {
    return this.#client.asset<T>(this.id, assetId, signal);
  }

  assetEffect<T>(assetId: string, signal?: AbortSignal) {
    return this.#client.assetEffect<T>(this.id, assetId, signal);
  }

  promptEffect<T>(query: string, options?: GenerateOptions) {
    const model = options?.model ?? this.#model;
    return this.#client.generateEffect<T>(this.id, query, model ? { ...options, model } : options);
  }

  prompt(
    query: string,
    options?: GenerateOptions & { schema?: undefined },
  ): Promise<GenerateResult>;
  prompt<T>(
    query: string,
    options: GenerateOptions & { schema: StructuredSchema<T> },
  ): Promise<GenerateResult<T>>;
  prompt<T = undefined>(query: string, options?: GenerateOptions): Promise<GenerateResult<T>> {
    const model = options?.model ?? this.#model;
    const merged = model ? { ...options, model } : options;
    if (merged?.schema !== undefined) {
      return this.#client.generate(
        this.id,
        query,
        merged as GenerateOptions & { schema: StructuredSchema<unknown> },
      ) as Promise<GenerateResult<T>>;
    }
    return this.#client.generate(
      this.id,
      query,
      merged as GenerateOptions & { schema?: undefined },
    ) as Promise<GenerateResult<T>>;
  }

  streamEffect(
    query: string,
    options: CompletionOptions,
    emit: (event: CompletionEvent) => Effect.Effect<void, ClientHttpError>,
  ) {
    const model = options.model ?? this.#model;
    return this.#client.streamEffect(this.id, query, model ? { ...options, model } : options, emit);
  }

  stream(query: string, options?: CompletionOptions): AsyncGenerator<CompletionEvent> {
    const model = options?.model ?? this.#model;
    const merged = model ? { ...options, model } : options;
    return this.#client.stream(this.id, query, merged);
  }
}

/** React and other native consumers can compose SDK handles without starting a Promise runtime. */
function sessionReadEffect<A>(
  session: OkraSession,
  method: string,
  native: (session: OkraSessionHandle) => Effect.Effect<A, ClientHttpError>,
  foreign: (signal: AbortSignal) => Promise<A>,
  signal?: AbortSignal,
): Effect.Effect<A, ClientHttpError> {
  return Effect.andThen(
    Effect.yieldNow,
    withClientSignal(
      Effect.suspend(() =>
        session instanceof OkraSessionHandle
          ? native(session)
          : Effect.tryPromise({
              try: foreign,
              catch: (cause) => new ClientHttpError({ method, cause }),
            }),
      ),
      signal,
    ),
  );
}

export function sessionStatusEffect(session: OkraSession, signal?: AbortSignal) {
  return sessionReadEffect(
    session,
    'session_status',
    (native) => native.statusEffect(signal),
    (owned) => session.status(owned),
    signal,
  );
}

export function sessionPagesEffect(session: OkraSession, signal?: AbortSignal) {
  return sessionReadEffect(
    session,
    'session_pages',
    (native) => native.pagesEffect(signal),
    (owned) => session.pages({ signal: owned }),
    signal,
  );
}

export function sessionPageEffect(session: OkraSession, pageNumber: number, signal?: AbortSignal) {
  return sessionReadEffect(
    session,
    'session_page',
    (native) => native.pageEffect(pageNumber, signal),
    (owned) => session.page(pageNumber, owned),
    signal,
  );
}

/** SDK handles share only within their owning client; custom handles retain their own identity. */
export function sessionCacheOwner(session: OkraSession): object {
  return session instanceof OkraSessionHandle ? session.cacheOwner() : session;
}

export function sessionAssetEffect<T>(session: OkraSession, assetId: string, signal?: AbortSignal) {
  return sessionReadEffect(
    session,
    'session_asset',
    (native) => native.assetEffect<T>(assetId, signal),
    (owned) => session.asset<T>(assetId, owned),
    signal,
  );
}

export function sessionPromptEffect<T>(
  session: OkraSession,
  query: string,
  options: GenerateOptions = {},
) {
  return sessionReadEffect(
    session,
    'session_prompt',
    (native) => native.promptEffect<T>(query, options),
    (owned) =>
      session.prompt<T>(query, { ...options, signal: owned } as GenerateOptions & {
        schema: StructuredSchema<T>;
      }),
    options.signal,
  );
}

/** Native streaming for SDK sessions; custom generators retain per-pull and iterator cleanup adapters. */
export function sessionStreamEffect(
  session: OkraSession,
  query: string,
  options: CompletionOptions,
  onEvent: (event: CompletionEvent) => void,
): Effect.Effect<void, ClientHttpError> {
  const failure = (cause: unknown) => new ClientHttpError({ method: 'session_stream', cause });
  const consume = (event: CompletionEvent) =>
    Effect.try({ try: () => onEvent(event), catch: failure });
  const program = Effect.suspend(() =>
    session instanceof OkraSessionHandle
      ? session.streamEffect(query, options, consume)
      : Effect.acquireUseRelease(
          Effect.try({
            try: () => {
              const controller = new AbortController();
              const iterator = session.stream(query, { ...options, signal: controller.signal });
              return { controller, iterator };
            },
            catch: failure,
          }),
          (owner) =>
            Effect.gen(function* () {
              while (true) {
                const next = yield* Effect.tryPromise({
                  try: () => owner.iterator.next(),
                  catch: failure,
                });
                if (next.done) return;
                yield* consume(next.value);
              }
            }),
          (owner) =>
            Effect.gen(function* () {
              owner.controller.abort();
              // Abort before return: cooperative generators can release a pending pull.
              // An adapter that ignores abort may finish its pending next/return later.
              yield* Effect.tryPromise({
                try: () => owner.iterator.return(undefined),
                catch: failure,
              }).pipe(Effect.ignore);
            }),
        ),
  );
  return Effect.andThen(Effect.yieldNow, withClientSignal(program, options.signal));
}

/** Compose a phase-specific foreign signal without introducing another Promise runtime. */
function withClientSignal<A>(
  program: Effect.Effect<A, ClientHttpError>,
  signal?: AbortSignal,
  onAbort: () => Effect.Effect<never, ClientHttpError> = () => Effect.interrupt,
): Effect.Effect<A, ClientHttpError> {
  if (!signal) return program;
  return Effect.suspend(() => {
    if (signal.aborted) return onAbort();
    let listener: (() => void) | undefined;
    const aborted = Effect.callback<never, ClientHttpError>((resume) => {
      const abort = () => resume(onAbort());
      listener = abort;
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) abort();
    }).pipe(
      Effect.ensuring(
        Effect.sync(() => {
          if (listener) signal.removeEventListener('abort', listener);
        }),
      ),
    );
    return Effect.raceFirst(program, aborted);
  });
}

export function withFetchedResponseEffect<A>(
  init: RequestInit,
  fetchResponse: (init: RequestInit) => Promise<Response>,
  use: (response: Response) => Effect.Effect<A, ClientHttpError>,
): Effect.Effect<A, ClientHttpError> {
  const failure = (cause: unknown) =>
    new ClientHttpError({
      method: 'request_response',
      ...(cause instanceof OkraRuntimeError ? { status: cause.status } : {}),
      cause,
    });
  return Effect.acquireUseRelease(
    Effect.sync(() => ({
      controller: new AbortController(),
      response: undefined as Response | undefined,
    })),
    (owner) =>
      Effect.gen(function* () {
        const signal = init.signal
          ? AbortSignal.any([owner.controller.signal, init.signal])
          : owner.controller.signal;
        if (signal.aborted) return yield* Effect.interrupt;
        const response = yield* Effect.tryPromise({
          // This minimal async bridge adopts or discards a foreign response.
          try: async () => {
            const response = await fetchResponse({ ...init, signal });
            if (signal.aborted) {
              await response.body?.cancel().catch(() => undefined);
              throw new Error('SDK response arrived after cancellation');
            }
            owner.response = response;
            return response;
          },
          catch: failure,
        });
        return yield* use(response);
      }),
    (owner) =>
      Effect.gen(function* () {
        owner.controller.abort();
        if (owner.response?.body && !owner.response.body.locked) {
          yield* Effect.tryPromise(() => owner.response!.body!.cancel()).pipe(Effect.ignore);
        }
      }),
  );
}

// ─── Client ──────────────────────────────────────────────────────────────────

export class OkraClient {
  private readonly baseUrl: string;
  private readonly apiKey?: string;
  private readonly sharedSecret?: string;
  private readonly fetchImpl: typeof globalThis.fetch;
  private documentStatusRoute: 'v1' | 'document' = 'v1';
  readonly sessions: {
    create: (sourceOrDocId: UploadInput, options?: SessionCreateOptions) => Promise<OkraSession>;
    from: (documentId: string, options?: SessionAttachOptions) => OkraSession;
  };
  readonly collections: {
    list: (signal?: AbortSignal) => Promise<CollectionSummary[]>;
    get: (collectionId: string, signal?: AbortSignal) => Promise<Collection>;
    query: <T = undefined>(
      collectionId: string,
      prompt: string,
      options?: CollectionQueryOptions<T>,
    ) => CollectionQueryStream<T>;
    exportMarkdown: (
      collectionId: string,
      options?: AbortSignal | CollectionExportOptions,
    ) => Promise<CollectionMarkdownExport | Uint8Array>;
  };
  readonly files: OkraFiles;
  readonly resources: {
    list: (signal?: AbortSignal) => Promise<ApiResourceCatalog>;
    get: (name: string, signal?: AbortSignal) => Promise<ApiResource | ApiAction>;
  };
  readonly context: OkraContextClient;
  readonly contextEffect: OkraContextEffectClient;
  readonly workflows: {
    run: (
      definition: DynamicWorkflowDefinition,
      options?: DynamicWorkflowRunOptions,
    ) => Promise<DynamicWorkflowRun>;
    getRun: (runId: string, signal?: AbortSignal) => Promise<DynamicWorkflowRun>;
    listRuns: (options?: DynamicWorkflowRunListOptions) => Promise<DynamicWorkflowRunList>;
  };
  readonly components: {
    compile: (component: ComponentNode) => ReturnType<typeof compileComponent>;
    run: (
      component: ComponentNode,
      options?: DynamicWorkflowRunOptions,
    ) => Promise<DynamicWorkflowRun>;
  };
  constructor(options: OkraClientOptions) {
    this.baseUrl = normalizeBaseUrl(options.baseUrl || DEFAULT_BASE_URL);
    this.apiKey = options.apiKey;
    this.sharedSecret = options.sharedSecret;
    this.fetchImpl = options.fetch || globalThis.fetch.bind(globalThis);

    if (!this.apiKey && !this.sharedSecret) {
      throw new OkraRuntimeError(
        'UNAUTHORIZED',
        'OkraClient requires either apiKey or sharedSecret',
        401,
      );
    }

    if (
      typeof globalThis !== 'undefined' &&
      'window' in globalThis &&
      this.apiKey &&
      !this.apiKey.startsWith('okra_pk_')
    ) {
      console.warn(
        '[okraPDF] Secret API key detected in browser. Avoid exposing secret keys in client-side code. ' +
          'Use a server-side proxy or another trusted backend boundary for browser apps.',
      );
    }

    this.sessions = {
      create: (sourceOrDocId, sessionOptions = {}) =>
        this.runHttpEffect(this.createSessionEffect(sourceOrDocId, sessionOptions)),
      from: (documentId, sessionOptions = {}) => {
        const normalized = documentId.trim();
        if (!normalized) {
          throw new OkraRuntimeError(
            'INVALID_REQUEST',
            'sessions.from requires a non-empty documentId',
            400,
          );
        }

        return new OkraSessionHandle(this, normalized, sessionOptions.model?.trim() || undefined);
      },
    };

    this.collections = {
      list: (signal) => this.collectionList(signal),
      get: (collectionId, signal) => this.collectionGet(collectionId, signal),
      query: <T = undefined>(
        collectionId: string,
        prompt: string,
        options?: CollectionQueryOptions<T>,
      ) => this.collectionQuery<T>(collectionId, prompt, options),
      exportMarkdown: (collectionId, options?) =>
        this.collectionExportMarkdown(collectionId, options),
    };

    this.files = {
      upload: (input, uploadOptions) => this.fileUpload(input, uploadOptions),
      get: (fileId, signal) => this.getFile(fileId, signal),
      list: (listOptions) => this.listFiles(listOptions),
      delete: (fileId, signal) => this.deleteFile(fileId, signal),
      downloadUrl: (fileId) => this.fileDownloadUrl(fileId),
    };

    this.resources = {
      list: (signal) => this.listResources(signal),
      get: (name, signal) => this.getResource(name, signal),
    };

    this.contextEffect = createHttpContextEffectClient({
      baseUrl: this.baseUrl,
      apiKey: this.apiKey,
      sharedSecret: this.sharedSecret,
      fetch: this.fetchImpl,
    });
    this.context = contextEffectClientToPromise(this.contextEffect);

    this.workflows = {
      run: (definition, runOptions) => this.runDynamicWorkflow(definition, runOptions),
      getRun: (runId, signal) => this.getDynamicWorkflowRun(runId, signal),
      listRuns: (listOptions) => this.listDynamicWorkflowRuns(listOptions),
    };
    this.components = {
      compile: (component) => compileComponent(component),
      run: (component, runOptions) =>
        this.runDynamicWorkflow(compileComponent(component).workflow, runOptions),
    };
  }

  createSessionEffect = Effect.fn('sdk.createSession')(function* (
    this: OkraClient,
    sourceOrDocId: UploadInput,
    options: SessionCreateOptions,
  ) {
    const client = this;
    const failure = (cause: unknown) => new ClientHttpError({ method: 'create_session', cause });
    return yield* Effect.acquireUseRelease(
      Effect.sync(() => ({ session: undefined as OkraSession | undefined })),
      (owner) =>
        Effect.gen(function* () {
          const session =
            typeof sourceOrDocId === 'string' && isDocumentId(sourceOrDocId.trim())
              ? yield* Effect.try({
                  try: () => client.sessions.from(sourceOrDocId.trim(), { model: options.model }),
                  catch: failure,
                })
              : yield* withClientSignal(
                  client.uploadEffect(sourceOrDocId, options.upload ?? {}, owner),
                  options.upload?.signal,
                );
          owner.session = session;
          const model = options.model?.trim();
          if (model)
            yield* Effect.tryPromise({ try: () => session.setModel(model), catch: failure });
          if (options.wait ?? true) {
            const wait = options.waitOptions ?? {};
            yield* withClientSignal(client.waitEffect(session.id, wait), wait.signal, () =>
              Effect.fail(failure(new OkraRuntimeError('TIMEOUT', 'Wait aborted', 499))),
            );
          }
          return session;
        }),
      (owner, exit) =>
        Effect.sync(() => {
          if (owner.session && (Exit.isFailure(exit) || (options.wait ?? true))) {
            uploadProgressStreams.get(owner.session)?.close();
            uploadProgressStreams.delete(owner.session);
          }
        }),
    );
  });

  // ─── API Resources ──────────────────────────────────────────────────────

  async listResources(signal?: AbortSignal): Promise<ApiResourceCatalog> {
    return this.requestJson<ApiResourceCatalog>('/v1/resources', {
      method: 'GET',
      signal,
    });
  }

  async getResource(name: string, signal?: AbortSignal): Promise<ApiResource | ApiAction> {
    const normalized = name.trim();
    if (!normalized) {
      throw new OkraRuntimeError(
        'INVALID_REQUEST',
        'resources.get requires a non-empty resource name',
        400,
      );
    }
    return this.requestJson<ApiResource | ApiAction>(
      `/v1/resources/${encodeURIComponent(normalized)}`,
      {
        method: 'GET',
        signal,
      },
    );
  }

  // ─── Collections ────────────────────────────────────────────────────────

  private async collectionList(signal?: AbortSignal): Promise<CollectionSummary[]> {
    const res = await this.requestJson<{ collections: CollectionSummary[] }>('/v1/collections', {
      method: 'GET',
      signal,
    });
    return res.collections;
  }

  private async collectionGet(collectionId: string, signal?: AbortSignal): Promise<Collection> {
    return this.requestJson<Collection>(`/v1/collections/${encodeURIComponent(collectionId)}`, {
      method: 'GET',
      signal,
    });
  }

  private collectionQuery<T = undefined>(
    collectionId: string,
    prompt: string,
    options?: CollectionQueryOptions<T>,
  ): CollectionQueryStream<T> {
    const ac = new AbortController();

    const body: Record<string, unknown> = { prompt, stream: true };
    if (options?.schema) {
      const normalized = normalizeStructuredSchema(options.schema);
      body.response_format = {
        type: 'json_schema',
        json_schema: { name: 'result', schema: normalized.jsonSchema },
      };
    }
    if (options?.cite) {
      body.cite = true;
    }
    if (options?.docIds) {
      body.doc_ids = options.docIds;
    }

    const signal = options?.signal ? AbortSignal.any([ac.signal, options.signal]) : ac.signal;
    const owner = createClientEventStream<CollectionQueryEvent, ClientHttpError>(
      (emit) =>
        this.withResponseEffect(
          `/v1/collections/${encodeURIComponent(collectionId)}/query`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            signal,
          },
          Effect.fn('sdk.collectionQuery')(function* (response) {
            if (!response.ok) {
              const text = yield* readClientResponseTextEffect(response);
              return yield* new ClientHttpError({
                method: 'collection_query',
                status: response.status,
                cause: new OkraRuntimeError(
                  'HTTP_ERROR',
                  `Collection query failed: ${text}`,
                  response.status,
                ),
              });
            }
            if (!response.body) {
              return yield* new ClientHttpError({
                method: 'collection_query',
                cause: new OkraRuntimeError(
                  'INVALID_RESPONSE',
                  'No response body for collection query',
                  500,
                ),
              });
            }
            yield* readClientResponseLinesEffect(
              response,
              (line) =>
                Effect.suspend(() => {
                  if (!line.trim()) return Effect.void;
                  let event: CollectionQueryEvent;
                  try {
                    event = JSON.parse(line) as CollectionQueryEvent;
                  } catch {
                    return Effect.void;
                  }
                  return emit(event);
                }),
              true,
            );
          }),
        ),
      {
        eager: true,
        signal,
        abortError: () => new ClientHttpError({ method: 'collection_query', cause: signal.reason }),
        restoreError: (error) => error.cause,
      },
    );
    const gather = Effect.fn('sdk.collectionQuery.gather')(function* () {
      const startTime = yield* Clock.currentTimeMillis;
      const answers = new Map<string, DocumentAnswer<T>>();
      let queryId = '';
      let queryPrompt = prompt;
      let totalCostUsd = 0;
      let completed = 0;
      let failed = 0;
      while (true) {
        const next = yield* owner.next();
        if (next.done) break;
        const event = next.value;
        if (event.type === 'start') {
          queryId = event.query_id;
          queryPrompt = event.prompt;
        } else if (event.type === 'result') {
          answers.set(event.doc_id, {
            docId: event.doc_id,
            status: event.status,
            answer: event.answer,
            data: event.data as T | undefined,
            ...(event.citations ? { citations: event.citations } : {}),
            costUsd: event.usage?.cost_usd ?? 0,
            durationMs: event.duration_ms,
            error: event.error,
          });
        } else if (event.type === 'done') {
          totalCostUsd = event.total_cost_usd;
          completed = event.completed;
          failed = event.failed;
        } else if (event.type === 'error') {
          return yield* new ClientHttpError({
            method: 'collection_gather',
            cause: new OkraRuntimeError('HTTP_ERROR', event.error, 500),
          });
        }
      }
      const finishedAt = yield* Clock.currentTimeMillis;
      return {
        queryId,
        prompt: queryPrompt,
        answers,
        totalCostUsd,
        durationMs: finishedAt - startTime,
        completed,
        failed,
      };
    }, Effect.ensuring(owner.close));
    return {
      [Symbol.asyncIterator]: () => owner.iterator,
      gather: () => this.runHttpEffect(gather()),
      abort: () => ac.abort(),
      toReadableStream: () => {
        const encoder = new TextEncoder();
        let cancelled = false;
        return new ReadableStream<Uint8Array>({
          async pull(controller) {
            try {
              const next = await owner.iterator.next();
              if (cancelled) return;
              if (next.done) controller.close();
              else controller.enqueue(encoder.encode(JSON.stringify(next.value) + '\n'));
            } catch (error) {
              if (!cancelled) controller.error(error);
            }
          },
          async cancel(reason) {
            cancelled = true;
            ac.abort(reason);
            await owner.iterator.return(undefined);
          },
        });
      },
    };
  }

  // ─── Collection Export (markdown from R2) ────────────────────────────────

  private collectionExportPath(collectionId: string, format: CollectionExportFormat): string {
    return `/v1/collections/${encodeURIComponent(collectionId)}/export?format=${encodeURIComponent(format)}`;
  }

  private async collectionExportMarkdown(
    collectionId: string,
    options?: AbortSignal | CollectionExportOptions,
  ): Promise<CollectionMarkdownExport | Uint8Array> {
    let format: CollectionExportFormat = 'markdown';
    let signal: AbortSignal | undefined;

    if (options && typeof options === 'object' && 'aborted' in options) {
      signal = options as AbortSignal;
    } else if (options) {
      format = options.format ?? 'markdown';
      signal = options.signal;
    }

    const client = this;
    return this.runHttpEffect(
      this.withResponseEffect(
        this.collectionExportPath(collectionId, format),
        { method: 'GET', signal },
        Effect.fn('sdk.collectionExport')(function* (response): Effect.fn.Return<
          CollectionMarkdownExport | Uint8Array,
          ClientHttpError
        > {
          if (!response.ok) {
            const text = yield* readClientResponseTextEffect(response);
            return yield* new ClientHttpError({
              method: 'collection_export',
              status: response.status,
              cause: new OkraRuntimeError(
                'HTTP_ERROR',
                `Collection export failed: ${text}`,
                response.status,
              ),
            });
          }
          if (format === 'zip') return yield* readClientResponseBytesEffect(response);
          if (!response.body) {
            return yield* new ClientHttpError({
              method: 'collection_export',
              cause: new OkraRuntimeError(
                'INVALID_RESPONSE',
                'No response body for collection export',
                500,
              ),
            });
          }
          const documents: DocumentMarkdownExport[] = [];
          let totalPages = 0;
          const consumeLine = (line: string) => {
            const trimmed = line.trim();
            if (!trimmed) return;
            try {
              const event = JSON.parse(trimmed) as CollectionExportEvent;
              if (event.type === 'result') {
                documents.push({
                  docId: event.doc_id,
                  fileName: event.file_name,
                  pageCount: event.page_count,
                  pages: event.pages,
                });
                totalPages += event.page_count;
              }
            } catch {
              // Preserve the export protocol's malformed-line tolerance.
            }
          };
          yield* withClientResponseReaderEffect(response, (reader) =>
            Effect.gen(function* () {
              const decoder = new TextDecoder();
              let buffer = '';
              while (true) {
                const chunk = yield* Effect.tryPromise({
                  try: () => reader.read(),
                  catch: (cause) => new ClientHttpError({ method: 'collection_export', cause }),
                });
                if (chunk.done) break;
                buffer += decoder.decode(chunk.value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';
                for (const line of lines) consumeLine(line);
              }
              consumeLine(buffer + decoder.decode());
            }),
          );
          const collectionName = yield* client
            .requestJsonEffect<Collection>(`/v1/collections/${encodeURIComponent(collectionId)}`, {
              method: 'GET',
              signal,
            })
            .pipe(
              Effect.map((collection) => collection.name),
              Effect.catch(() => Effect.succeed(collectionId)),
            );
          const now = yield* Clock.currentTimeMillis;
          return {
            collectionId,
            collectionName,
            documents,
            totalDocuments: documents.length,
            totalPages,
            exportedAt: new Date(now).toISOString(),
          };
        }),
      ),
      signal,
    );
  }

  // ─── Upload ──────────────────────────────────────────────────────────────

  upload(input: UploadInput, options: UploadOptions = {}): Promise<OkraSession> {
    return this.runHttpEffect(this.uploadEffect(input, options), options.signal);
  }

  private uploadEffect = Effect.fn('sdk.uploadDocument')(function* (
    this: OkraClient,
    input: UploadInput,
    options: UploadOptions,
    sessionOwner?: { session: OkraSession | undefined },
  ) {
    if (options.signal?.aborted) return yield* Effect.interrupt;
    const client = this;
    const documentId = options.documentId || makeDocId();
    const failure = (cause: unknown) => new ClientHttpError({ method: 'upload_document', cause });
    return yield* Effect.acquireUseRelease(
      Effect.try({
        try: () => {
          if (!options.onProgress) return undefined;
          const stream = client.events(documentId, { signal: options.signal });
          stream.on('*', (event) => {
            try {
              options.onProgress!(event);
            } finally {
              const phase = event.data?.phase;
              if (
                event.type === 'document.ready' ||
                event.type === 'document.error' ||
                (event.type === 'document.phase_changed' &&
                  typeof phase === 'string' &&
                  (COMPLETE_PHASES.has(phase) || TERMINAL_ERROR_PHASES.has(phase)))
              )
                stream.close();
            }
          });
          return stream;
        },
        catch: failure,
      }),
      (stream) =>
        Effect.gen(function* () {
          if (stream) yield* Effect.try({ try: () => stream.connect(), catch: failure });
          yield* client.uploadSourceEffect(documentId, input, options);
          const session = yield* Effect.try({
            try: () => client.sessions.from(documentId),
            catch: failure,
          });
          // The progress subscription outlives upload acceptance and ends on terminal progress,
          // caller abort, or the owning sessions.create wait. It is never a public data field.
          if (stream) uploadProgressStreams.set(session, stream);
          // Transfer ownership before the native stage returns, even if its caller is interrupted.
          if (sessionOwner) sessionOwner.session = session;
          return session;
        }),
      (stream, exit) => (Exit.isFailure(exit) ? Effect.sync(() => stream?.close()) : Effect.void),
    );
  });

  private uploadSourceEffect = Effect.fn('sdk.uploadSource')(function* (
    this: OkraClient,
    documentId: string,
    input: UploadInput,
    options: UploadOptions,
  ) {
    const path = '/document/' + encodeURIComponent(documentId);
    const visibility = options.visibility || 'private';
    if (typeof input === 'string' && isHttpUrl(input)) {
      const urlHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      if (options.vendorKeys) {
        urlHeaders['X-Vendor-Keys'] = JSON.stringify(options.vendorKeys);
      }
      yield* this.requestJsonEffect<{ phase?: string }>(`${path}/upload-url`, {
        method: 'POST',
        signal: options.signal,
        headers: urlHeaders,
        body: JSON.stringify({
          url: input,
          capabilities: options.capabilities,
          visibility,
          redact: options.redact,
          ...(options.intent ? { intent: options.intent } : {}),
          ...(options.config ? { config: options.config } : {}),
          ...(options.vendorOptions ? { vendor_options: options.vendorOptions } : {}),
        }),
      });
      return;
    }

    const resolved = yield* this.resolveBinaryUploadEffect(input, options.fileName);
    const bytes = resolved.bytes;
    const fileName = resolved.fileName;

    const headers: Record<string, string> = {
      'Content-Type': 'application/pdf',
      'X-File-Name': toHeaderSafeFileName(fileName),
    };
    if (options.capabilities) {
      headers['X-Capabilities'] = JSON.stringify(options.capabilities);
    }
    if (options.vendorKeys) {
      headers['X-Vendor-Keys'] = JSON.stringify(options.vendorKeys);
    }
    if (options.vendorOptions) {
      headers['X-Vendor-Options'] = JSON.stringify(options.vendorOptions);
    }
    if (options.redact) {
      headers['X-Redact'] = JSON.stringify(options.redact);
    }
    if (options.intent) {
      headers['X-Okra-Parse-Intent'] = options.intent
        .replace(/[\u0000-\u001f\u007f]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }
    if (options.config) {
      headers['X-Document-Config'] = JSON.stringify(options.config);
    }
    if (visibility === 'public') {
      headers['X-Visibility'] = 'public';
    }

    yield* this.requestJsonEffect<{ phase?: string }>(`${path}/upload`, {
      method: 'POST',
      signal: options.signal,
      headers,
      body: bytes as unknown as BodyInit,
    });
  });

  // ─── Passive Files ───────────────────────────────────────────────────────

  private fileUpload(input: UploadInput, options: FileUploadOptions = {}): Promise<OkraFile> {
    return this.runHttpEffect(this.fileUploadEffect(input, options), options.signal);
  }

  private fileUploadEffect = Effect.fn('sdk.fileUpload')(function* (
    this: OkraClient,
    input: UploadInput,
    options: FileUploadOptions,
  ) {
    if (options.signal?.aborted) return yield* Effect.interrupt;
    if (typeof input === 'string' && isHttpUrl(input)) {
      return yield* new ClientHttpError({
        method: 'file_upload',
        cause: new OkraRuntimeError(
          'INVALID_REQUEST',
          'files.upload only accepts local files, File/Blob, ArrayBuffer, or Uint8Array. Remote URL ingestion still belongs to document/session upload.',
          400,
        ),
      });
    }

    const resolved = yield* this.resolveBinaryUploadEffect(input, options.fileName);
    const bytes = resolved.bytes;
    const fileName = normalizePdfFileName(resolved.fileName);
    const transport = this.chooseFileTransport(bytes, options.transport);

    if (transport === 'multipart') {
      return yield* this.fileUploadMultipartEffect(bytes, fileName, options);
    }

    return yield* this.fileUploadDirectEffect(bytes, fileName, options);
  });

  private chooseFileTransport(
    bytes: Uint8Array,
    transport: FileUploadOptions['transport'] = 'auto',
  ): Exclude<FileUploadOptions['transport'], 'auto'> {
    if (transport === 'multipart' || transport === 'direct') {
      return transport;
    }
    return bytes.byteLength > DEFAULT_FILE_DIRECT_UPLOAD_THRESHOLD_BYTES ? 'direct' : 'multipart';
  }

  private fileUploadMultipartEffect = Effect.fn('sdk.fileUploadMultipart')(function* (
    this: OkraClient,
    bytes: Uint8Array,
    fileName: string,
    options: FileUploadOptions,
  ) {
    const formData = yield* Effect.try({
      try: () => {
        const formData = new FormData();
        formData.append(
          'file',
          new Blob([uint8ArrayToArrayBuffer(bytes)], { type: 'application/pdf' }),
          fileName,
        );
        if (options.config) {
          formData.append('config', JSON.stringify(options.config));
        }

        return formData;
      },
      catch: (cause) => new ClientHttpError({ method: 'file_form', cause }),
    });

    return yield* this.requestJsonEffect<OkraFile>('/v1/files', {
      method: 'POST',
      body: formData,
      signal: options.signal,
    });
  });

  private fileUploadDirectEffect = Effect.fn('sdk.fileUploadDirect')(function* (
    this: OkraClient,
    bytes: Uint8Array,
    fileName: string,
    options: FileUploadOptions,
  ) {
    const sha256 = yield* sha256HexFromBytesEffect(bytes);
    const presign = yield* this.requestJsonEffect<OkraFile | OkraFileUploadSession>(
      '/v1/files/presign',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName,
          fileSize: bytes.byteLength,
          sha256,
        }),
        signal: options.signal,
      },
    );

    if ((presign as { object?: string }).object === 'file') {
      return presign as OkraFile;
    }

    const uploadSession = presign as OkraFileUploadSession;
    yield* withFetchedResponseEffect(
      {
        method: uploadSession.upload_method,
        headers: uploadSession.upload_headers,
        body: bytes as unknown as BodyInit,
        signal: options.signal,
      },
      (init) => this.fetchAbsolutePromise(uploadSession.upload_url, init),
      Effect.fn('sdk.fileUploadResponse')(function* (uploadResponse) {
        if (!uploadResponse.ok) {
          const detail = yield* readClientResponseTextEffect(uploadResponse).pipe(
            Effect.catch(() => Effect.succeed('')),
          );
          return yield* new ClientHttpError({
            method: 'file_upload_response',
            status: uploadResponse.status,
            cause: new OkraRuntimeError(
              'HTTP_ERROR',
              detail || `Direct file upload failed with status ${uploadResponse.status}`,
              uploadResponse.status,
            ),
          });
        }
      }),
    );

    return yield* this.requestJsonEffect<OkraFile>('/v1/files/finalize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: uploadSession.id,
        fileName,
        r2Key: uploadSession.r2_key,
        sha256,
        ...(options.config ? { config: JSON.stringify(options.config) } : {}),
      }),
      signal: options.signal,
    });
  });

  // ─── Stateless parse (POST /v1/parse) ────────────────────────────────────

  parse(opts: ParseOptions): Promise<ParseJob> {
    return this.runHttpEffect(this.parseEffect(opts), opts?.signal);
  }

  private parseEffect = Effect.fn('sdk.parse')(function* (this: OkraClient, opts: ParseOptions) {
    if (opts?.signal?.aborted) return yield* Effect.interrupt;
    if (!opts || (opts.file === undefined && !opts.fileId)) {
      return yield* new ClientHttpError({
        method: 'parse',
        cause: new OkraRuntimeError(
          'INVALID_REQUEST',
          'parse requires either `file` (bytes/path/Blob) or `fileId` (existing /v1/files entry)',
          400,
        ),
      });
    }
    if (opts.file !== undefined && opts.fileId) {
      return yield* new ClientHttpError({
        method: 'parse',
        cause: new OkraRuntimeError(
          'INVALID_REQUEST',
          'parse accepts either `file` or `fileId`, not both',
          400,
        ),
      });
    }

    const parser =
      opts.parser && typeof opts.parser === 'object'
        ? {
            id: opts.parser.id,
            ...(opts.parser.variant ? { variant: opts.parser.variant } : {}),
            ...(opts.parser.options ? { options: opts.parser.options } : {}),
            ...(opts.parser.vendorOptions || opts.parser.vendor_options
              ? { vendor_options: opts.parser.vendor_options ?? opts.parser.vendorOptions }
              : {}),
          }
        : (opts.parser ?? 'textlayer');

    const body: Record<string, unknown> = { parser };
    if (opts.variant) body.variant = opts.variant;
    if (opts.pages !== undefined) body.pages = opts.pages;
    if (opts.outputs !== undefined) body.outputs = opts.outputs;
    if (opts.publish !== undefined) body.publish = opts.publish;
    if (opts.options) body.options = opts.options;
    if (opts.vendorOptions) body.vendor_options = opts.vendorOptions;
    if (opts.metadata) body.metadata = opts.metadata;
    if (opts.schema !== undefined) body.schema = opts.schema;

    if (opts.fileId) {
      body.file = { id: opts.fileId };
      if (opts.fileName) body.file_name = opts.fileName;
    } else {
      const resolved = yield* this.resolveBinaryUploadEffect(
        opts.file as UploadInput,
        opts.fileName,
      );
      body.file = {
        data: yield* Effect.try({
          try: () => bytesToBase64(resolved.bytes),
          catch: (cause) => new ClientHttpError({ method: 'parse_input', cause }),
        }),
      };
      body.file_name = opts.fileName ?? resolved.fileName;
    }

    return (yield* protocolParseEffect(
      {
        baseUrl: this.baseUrl,
        getHeaders: () => this.authHeaders(),
        fetch: this.fetchImpl,
      },
      body as ParseRequest,
    ).pipe(
      Effect.mapError(
        (error) =>
          new ClientHttpError({
            method: 'parse',
            status: error.status,
            cause: new OkraRuntimeError(
              error.status === 401 ? 'UNAUTHORIZED' : 'HTTP_ERROR',
              error.message,
              error.status ?? 500,
              error.details,
            ),
          }),
      ),
    )) as ParseJob;
  });

  getJob(jobId: string, signal?: AbortSignal): Promise<OkraJob> {
    return this.runHttpEffect(this.getJobEffect(jobId, signal));
  }

  /** Native job lookup. Interruption joins response cleanup before the operation ends. */
  getJobEffect = Effect.fn('sdk.getJob')(function* (
    this: OkraClient,
    jobId: string,
    signal?: AbortSignal,
  ) {
    const normalized = yield* Effect.try({
      try: () => {
        const id = jobId.trim();
        if (!id)
          throw new OkraRuntimeError('INVALID_REQUEST', 'jobs.get requires a non-empty jobId', 400);
        return id;
      },
      catch: (cause) => new ClientHttpError({ method: 'get_job', cause }),
    });
    return yield* this.requestEffect<OkraJob>(`/v1/jobs/${encodeURIComponent(normalized)}`, {
      method: 'GET',
      signal,
    });
  });

  async createJob(options: CreateJobOptions): Promise<OkraJob> {
    if (!options || typeof options !== 'object') {
      throw new OkraRuntimeError('INVALID_REQUEST', 'jobs.create requires an options object', 400);
    }
    return this.requestJson<OkraJob>('/v1/jobs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
      signal: options.signal,
    });
  }

  listJobs(options: JobListOptions = {}): Promise<JobListResponse> {
    return this.runHttpEffect(this.listJobsEffect(options));
  }

  /** Native listing for callers which compose lookup and polling in one lifetime. */
  listJobsEffect = Effect.fn('sdk.listJobs')(function* (
    this: OkraClient,
    options: JobListOptions = {},
  ) {
    const params = new URLSearchParams();
    if (options.type) params.set('type', options.type);
    if (options.status) params.set('status', options.status);
    if (options.documentId) params.set('document_id', options.documentId);
    if (typeof options.limit === 'number') params.set('limit', String(options.limit));
    const query = params.toString();
    return yield* this.requestEffect<JobListResponse>(`/v1/jobs${query ? `?${query}` : ''}`, {
      method: 'GET',
      signal: options.signal,
    });
  });

  async getFile(fileId: string, signal?: AbortSignal): Promise<OkraFile> {
    const normalized = fileId.trim();
    if (!normalized) {
      throw new OkraRuntimeError('INVALID_REQUEST', 'files.get requires a non-empty fileId', 400);
    }
    return this.requestJson<OkraFile>(`/v1/files/${encodeURIComponent(normalized)}`, {
      method: 'GET',
      signal,
    });
  }

  async listFiles(options: FileListOptions = {}): Promise<OkraFileListResponse> {
    const params = new URLSearchParams();
    if (typeof options.limit === 'number') params.set('limit', String(options.limit));
    if (options.cursor) params.set('cursor', options.cursor);
    const query = params.toString();
    return this.requestJson<OkraFileListResponse>(`/v1/files${query ? `?${query}` : ''}`, {
      method: 'GET',
      signal: options.signal,
    });
  }

  async deleteFile(fileId: string, signal?: AbortSignal): Promise<DeleteFileResult> {
    const normalized = fileId.trim();
    if (!normalized) {
      throw new OkraRuntimeError(
        'INVALID_REQUEST',
        'files.delete requires a non-empty fileId',
        400,
      );
    }
    const client = this;
    return this.runHttpEffect(
      this.withResponseEffect(
        `/v1/files/${encodeURIComponent(normalized)}`,
        { method: 'DELETE', signal },
        Effect.fn('sdk.deleteFile')(function* (response) {
          if (!response.ok) {
            const text = yield* readClientResponseTextEffect(response);
            const parsed = client.parseBody(text);
            const message =
              getErrorMessage(
                parsed && typeof parsed === 'object' && 'error' in parsed
                  ? (parsed as { error?: unknown }).error
                  : undefined,
              ) || `Request failed with status ${response.status}`;
            return yield* new ClientHttpError({
              method: 'delete_file',
              status: response.status,
              cause: new OkraRuntimeError('HTTP_ERROR', message, response.status, parsed ?? text),
            });
          }
          return { deleted: true as const, fileId: normalized };
        }),
      ),
      signal,
    );
  }

  fileDownloadUrl(fileId: string): string {
    return `${this.baseUrl}/v1/files/${encodeURIComponent(fileId)}/bytes`;
  }

  // ─── Workflow Config / Reparse ───────────────────────────────────────────

  getConfig(documentId: string, signal?: AbortSignal): Promise<DocumentSpecRecord> {
    return this.runHttpEffect(this.getConfigEffect(documentId, signal), signal);
  }

  getConfigEffect = Effect.fn('sdk.getConfig')(function* (
    this: OkraClient,
    documentId: string,
    signal?: AbortSignal,
  ) {
    const path = yield* Effect.try({
      try: () => {
        if (!documentId.trim()) {
          throw new OkraRuntimeError(
            'INVALID_REQUEST',
            'getConfig requires a non-empty documentId',
            400,
          );
        }
        return `/document/${encodeURIComponent(documentId)}/config`;
      },
      catch: (cause) => new ClientHttpError({ method: 'get_config', cause }),
    });
    return yield* this.requestJsonEffect<DocumentSpecRecord>(path, { method: 'GET', signal });
  });

  updateConfig(
    documentId: string,
    update: DocumentConfigUpdate,
    signal?: AbortSignal,
  ): Promise<DocumentConfigResult> {
    return this.runHttpEffect(this.updateConfigEffect(documentId, update, signal), signal);
  }

  updateConfigEffect = Effect.fn('sdk.updateConfig')(function* (
    this: OkraClient,
    documentId: string,
    update: DocumentConfigUpdate,
    signal?: AbortSignal,
  ) {
    const request = yield* Effect.try({
      try: () => {
        if (!documentId.trim()) {
          throw new OkraRuntimeError(
            'INVALID_REQUEST',
            'updateConfig requires a non-empty documentId',
            400,
          );
        }
        return {
          path: `/document/${encodeURIComponent(documentId)}/config`,
          body: JSON.stringify(update),
        };
      },
      catch: (cause) => new ClientHttpError({ method: 'update_config', cause }),
    });
    return yield* this.requestJsonEffect<DocumentConfigResult>(request.path, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: request.body,
      signal,
    });
  });

  reparse(documentId: string, options: ReparseOptions = {}): Promise<ReparseResult> {
    return this.runHttpEffect(this.reparseEffect(documentId, options), options.signal);
  }

  private reparseEffect = Effect.fn('sdk.reparse')(function* (
    this: OkraClient,
    documentId: string,
    options: ReparseOptions,
  ) {
    const path = yield* Effect.try({
      try: () => {
        if (!documentId.trim()) {
          throw new OkraRuntimeError(
            'INVALID_REQUEST',
            'reparse requires a non-empty documentId',
            400,
          );
        }
        const strategy =
          options.strategy && options.strategy !== 'auto'
            ? `?strategy=${encodeURIComponent(options.strategy)}`
            : '';
        return `/document/${encodeURIComponent(documentId)}/reparse${strategy}`;
      },
      catch: (cause) => new ClientHttpError({ method: 'reparse', cause }),
    });
    return yield* this.requestJsonEffect<ReparseResult>(path, {
      method: 'POST',
      signal: options.signal,
    });
  });

  async verify(documentId: string, params: VerifyParams): Promise<VerifyResult> {
    if (!documentId.trim()) {
      throw new OkraRuntimeError('INVALID_REQUEST', 'verify requires a non-empty documentId', 400);
    }
    return this.requestJson<VerifyResult>(
      `/v1/documents/${encodeURIComponent(documentId)}/verify`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claim: params.claim,
          page: params.page,
          ...(params.bbox ? { bbox: params.bbox } : {}),
        }),
        signal: params.signal,
      },
    );
  }

  applyWorkflow(documentId: string, options: ApplyWorkflowOptions): Promise<ApplyWorkflowResult> {
    return this.runHttpEffect(this.applyWorkflowEffect(documentId, options), options?.signal);
  }

  private applyWorkflowEffect = Effect.fn('sdk.applyWorkflow')(function* (
    this: OkraClient,
    documentId: string,
    options: ApplyWorkflowOptions,
  ): Effect.fn.Return<ApplyWorkflowResult, ClientHttpError> {
    const failure = (cause: unknown) => new ClientHttpError({ method: 'apply_workflow', cause });
    if (!options?.capabilities) {
      return yield* failure(
        new OkraRuntimeError('INVALID_REQUEST', 'applyWorkflow requires capabilities', 400),
      );
    }

    const current = yield* this.getConfigEffect(documentId, options.signal);
    const update = yield* Effect.try({
      try: () => mergeCapabilitiesIntoDocumentSpec(current.spec, options.capabilities),
      catch: failure,
    });
    const config = yield* this.updateConfigEffect(documentId, update, options.signal);

    if (options.reparse === false) {
      return { config };
    }

    const reparse = yield* this.reparseEffect(documentId, {
      strategy: options.strategy,
      signal: options.signal,
    });
    return { config, reparse };
  });

  async getKeyWorkflow(signal?: AbortSignal): Promise<ApiKeyWorkflowConfigResponse> {
    return this.requestJson<ApiKeyWorkflowConfigResponse>('/v1/key-workflow', {
      method: 'GET',
      signal,
    });
  }

  async setKeyWorkflow(
    defaultCapabilities: ApiKeyWorkflowConfigResponse['default_capabilities'],
    signal?: AbortSignal,
  ): Promise<ApiKeyWorkflowConfigResponse> {
    return this.requestJson<ApiKeyWorkflowConfigResponse>('/v1/key-workflow', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ default_capabilities: defaultCapabilities }),
      signal,
    });
  }

  // ─── Dynamic Workflow Eval Runs ─────────────────────────────────────────

  runDynamicWorkflow(
    definition: DynamicWorkflowDefinition,
    options: DynamicWorkflowRunOptions = {},
  ): Promise<DynamicWorkflowRun> {
    return this.runHttpEffect(this.runDynamicWorkflowEffect(definition, options), options.signal);
  }

  private runDynamicWorkflowEffect = Effect.fn('sdk.runDynamicWorkflow')(function* (
    this: OkraClient,
    definition: DynamicWorkflowDefinition,
    options: DynamicWorkflowRunOptions,
  ): Effect.fn.Return<DynamicWorkflowRun, ClientHttpError> {
    const failure = (cause: unknown) =>
      new ClientHttpError({ method: 'run_dynamic_workflow', cause });
    const prepared = yield* restoreDynamicWorkflowError(
      prepareDynamicWorkflowEffect(definition),
    ).pipe(
      Effect.mapError((error) =>
        failure(
          new OkraRuntimeError(
            'INVALID_REQUEST',
            error instanceof Error ? error.message : String(error),
            400,
            error,
          ),
        ),
      ),
    );
    const workflow = prepared.definition;

    const dataset = options.dataset ?? workflow.eval?.dataset;
    const runPayload = {
      workflow,
      dataset,
      inputs: options.inputs ?? {},
      metadata: options.metadata ?? {},
      dry_run: Boolean(options.dryRun),
    };
    const body: Record<string, unknown> = {
      name: workflow.name,
      definition: {
        code: prepared.agentWorkflowSource,
      },
    };

    if (options.dryRun) {
      body.run = false;
      body.inputs = runPayload;
    } else {
      body.run = true;
      body.params = runPayload;
    }

    const serialized = yield* Effect.try({ try: () => JSON.stringify(body), catch: failure });
    const created = yield* this.requestJsonEffect<WorkflowResourceCreateResponse>('/v1/workflows', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: serialized,
      signal: options.signal,
    });

    if (created.run) {
      return yield* Effect.try({
        try: () => mapWorkflowResourceRun(created.run!, created.workflow_id ?? created.id, dataset),
        catch: failure,
      });
    }

    return {
      id: created.workflow_id ?? created.id,
      object: 'workflow_plan',
      status: 'planned',
      workflowId: created.workflow_id ?? created.id,
      ...(dataset ? { dataset } : {}),
      summary: {
        dryRun: Boolean(options.dryRun),
        workflow: created,
      },
      createdAt: created.created_at,
    };
  });

  async getDynamicWorkflowRun(runId: string, signal?: AbortSignal): Promise<DynamicWorkflowRun> {
    const normalized = runId.trim();
    if (!normalized) {
      throw new OkraRuntimeError(
        'INVALID_REQUEST',
        'getDynamicWorkflowRun requires a non-empty runId',
        400,
      );
    }
    const run = await this.requestJson<WorkflowResourceRunResponse>(
      `/v1/runs/${encodeURIComponent(normalized)}`,
      { method: 'GET', signal },
    );
    return mapWorkflowResourceRun(run);
  }

  async listDynamicWorkflowRuns(
    options: DynamicWorkflowRunListOptions = {},
  ): Promise<DynamicWorkflowRunList> {
    const query = new URLSearchParams();
    if (options.workflowId) query.set('workflow_id', options.workflowId);
    if (options.datasetId) query.set('dataset_id', options.datasetId);
    if (options.status) query.set('status', options.status);
    if (typeof options.limit === 'number') query.set('limit', String(options.limit));
    if (options.cursor) query.set('cursor', options.cursor);

    const suffix = query.toString();
    const response = await this.requestJson<WorkflowRunListResourceResponse>(
      `/v1/runs${suffix ? `?${suffix}` : ''}`,
      { method: 'GET', signal: options.signal },
    );

    return {
      object: 'list',
      data: (response.data ?? []).map((run) => mapWorkflowResourceRun(run, options.workflowId)),
      hasMore: Boolean(response.has_more),
      nextCursor: response.next_cursor ?? null,
    };
  }

  assets(documentId: string, signal?: AbortSignal): Promise<DocumentAsset[]> {
    return this.runHttpEffect(this.assetsEffect(documentId, signal), signal);
  }

  assetsEffect(documentId: string, signal?: AbortSignal) {
    return this.statusEffect(documentId, signal).pipe(
      Effect.map((status) => normalizeDocumentAssets(status.plugins ?? [])),
    );
  }

  asset<T = Record<string, unknown>>(
    documentId: string,
    assetId: string,
    signal?: AbortSignal,
  ): Promise<DocumentAsset<T> | null> {
    return this.runHttpEffect(this.assetEffect<T>(documentId, assetId, signal), signal);
  }

  assetEffect<T>(documentId: string, assetId: string, signal?: AbortSignal) {
    return this.assetsEffect(documentId, signal).pipe(
      Effect.map(
        (assets) =>
          (assets.find((candidate) => candidate.assetId === assetId) as
            | DocumentAsset<T>
            | undefined) ?? null,
      ),
    );
  }

  // ─── Status / Wait ───────────────────────────────────────────────────────

  status(documentId: string, signal?: AbortSignal): Promise<DocumentStatus> {
    return this.runHttpEffect(this.statusEffect(documentId, signal), signal);
  }

  statusEffect = Effect.fn('sdk.documentStatus')(function* (
    this: OkraClient,
    documentId: string,
    signal?: AbortSignal,
  ) {
    const path = '/document/' + encodeURIComponent(documentId) + '/status';
    const get = () =>
      this.requestJsonEffect<DocumentStatus>(path, { method: 'GET', signal }).pipe(
        Effect.map(normalizeDocumentStatus),
      );
    if (this.documentStatusRoute === 'document') return yield* get();
    return yield* get().pipe(
      Effect.catch((error) => {
        if (!(error.cause instanceof OkraRuntimeError) || error.cause.status !== 404)
          return Effect.fail(error);
        this.documentStatusRoute = 'document';
        return get();
      }),
    );
  });

  wait(documentId: string, options: WaitOptions = {}): Promise<DocumentStatus> {
    return this.runHttpEffect(this.waitEffect(documentId, options), options.signal).catch(
      (error) => {
        if (options.signal?.aborted && !(error instanceof OkraRuntimeError))
          throw new OkraRuntimeError('TIMEOUT', 'Wait aborted', 499);
        throw error;
      },
    );
  }

  private waitEffect = Effect.fn('sdk.waitDocument')(function* (
    this: OkraClient,
    documentId: string,
    options: WaitOptions,
  ) {
    if (options.signal?.aborted) return yield* Effect.interrupt;
    return yield* options.realtime
      ? this.waitRealtimeEffect(documentId, options)
      : this.waitPollEffect(documentId, options);
  });

  private waitPollEffect = Effect.fn('sdk.waitDocument.poll')(function* (
    this: OkraClient,
    documentId: string,
    options: WaitOptions,
  ) {
    const client = this;
    const timeoutMs = options.timeoutMs ?? DEFAULT_WAIT_TIMEOUT_MS;
    const interval = options.pollIntervalMs ?? DEFAULT_WAIT_POLL_MS;
    let latest: DocumentStatus | undefined;
    const poll = Effect.gen(function* () {
      while (true) {
        latest = yield* client.statusEffect(documentId, options.signal);
        if (COMPLETE_PHASES.has(latest.phase)) return latest;
        if (TERMINAL_ERROR_PHASES.has(latest.phase))
          return yield* new ClientHttpError({
            method: 'wait_document',
            cause: new OkraRuntimeError(
              'EXTRACTION_FAILED',
              'Document entered terminal error phase (' + latest.phase + ')',
              500,
              latest,
            ),
          });
        yield* Effect.sleep(interval);
      }
    });
    return yield* poll.pipe(
      Effect.timeoutOrElse({
        duration: timeoutMs,
        orElse: () =>
          Effect.fail(
            new ClientHttpError({
              method: 'wait_document',
              cause: new OkraRuntimeError(
                'TIMEOUT',
                'Timed out waiting for document ' + documentId + ' after ' + timeoutMs + 'ms',
                504,
                latest,
              ),
            }),
          ),
      }),
    );
  });

  private waitRealtimeEffect = Effect.fn('sdk.waitDocument.realtime')(function* (
    this: OkraClient,
    documentId: string,
    options: WaitOptions,
  ) {
    const timeoutMs = options.timeoutMs ?? DEFAULT_WAIT_TIMEOUT_MS;
    const failure = (cause: unknown) => new ClientHttpError({ method: 'wait_realtime', cause });
    const result = yield* Effect.acquireUseRelease(
      Effect.try({ try: () => this.events(documentId), catch: failure }),
      (stream) =>
        Effect.callback<import('./events').DocumentEvent, ClientHttpError>((resume) => {
          stream.on('*', (event) => {
            try {
              options.onProgress?.(event);
              if (event.type === 'document.ready') resume(Effect.succeed(event));
              else if (event.type === 'document.error')
                resume(
                  Effect.fail(
                    failure(
                      new OkraRuntimeError(
                        'EXTRACTION_FAILED',
                        (event.data.error as string) || 'Document entered error phase',
                        500,
                      ),
                    ),
                  ),
                );
            } catch (cause) {
              resume(Effect.fail(failure(cause)));
            }
          });
          try {
            stream.connect();
          } catch (cause) {
            resume(Effect.fail(failure(cause)));
          }
        }).pipe(Effect.timeoutOption(timeoutMs)),
      (stream) => Effect.sync(() => stream.close()),
    );
    if (Option.isNone(result)) return yield* this.waitPollEffect(documentId, options);
    const fallback = { phase: 'complete', ...result.value.data } as DocumentStatus;
    const status = yield* this.statusEffect(documentId, options.signal).pipe(
      Effect.catch(() => Effect.succeed(fallback)),
      Effect.timeoutOption(timeoutMs),
    );
    return Option.isSome(status) ? status.value : fallback;
  });

  /** Build a WebSocket URL for the document event protocol. */
  private buildWsUrl(documentId: string): string {
    const base = this.baseUrl.replace(/^http/, 'ws');
    const params = new URLSearchParams({ protocol: 'v1' });
    if (this.apiKey) params.set('apiKey', this.apiKey);
    if (this.sharedSecret) params.set('secret', this.sharedSecret);
    return `${base}/document/${encodeURIComponent(documentId)}?${params.toString()}`;
  }

  /** Open a real-time event stream for a document. */
  events(documentId: string, options?: DocumentEventStreamOptions): DocumentEventStream {
    return new DocumentEventStream(this.buildWsUrl(documentId), options);
  }

  // ─── Pages ───────────────────────────────────────────────────────────────

  pages(documentId: string, options?: { range?: string; signal?: AbortSignal }): Promise<Page[]> {
    return this.runHttpEffect(this.pagesEffect(documentId, options), options?.signal);
  }

  pagesEffect = Effect.fn('sdk.documentPages')(function* (
    this: OkraClient,
    documentId: string,
    options?: { range?: string; signal?: AbortSignal },
  ) {
    const path = yield* Effect.try({
      try: () => {
        const params = options?.range ? `?range=${encodeURIComponent(options.range)}` : '';
        return `/document/${encodeURIComponent(documentId)}/pages${params}`;
      },
      catch: (cause) => new ClientHttpError({ method: 'document_pages', cause }),
    });
    return yield* this.requestJsonEffect<Page[]>(path, {
      method: 'GET',
      signal: options?.signal,
    });
  });

  page(documentId: string, pageNumber: number, signal?: AbortSignal): Promise<Page> {
    return this.runHttpEffect(this.pageEffect(documentId, pageNumber, signal), signal);
  }

  pageEffect = Effect.fn('sdk.documentPage')(function* (
    this: OkraClient,
    documentId: string,
    pageNumber: number,
    signal?: AbortSignal,
  ) {
    const path = yield* Effect.try({
      try: () => `/document/${encodeURIComponent(documentId)}/page/${pageNumber}`,
      catch: (cause) => new ClientHttpError({ method: 'document_page', cause }),
    });
    return yield* this.requestJsonEffect<Page>(path, { method: 'GET', signal });
  });

  // ─── Download ──────────────────────────────────────────────────────────

  downloadUrl(documentId: string): string {
    return `${this.baseUrl}/document/${encodeURIComponent(documentId)}/download`;
  }

  // ─── Entities ────────────────────────────────────────────────────────────

  async entities(
    documentId: string,
    options?: { type?: string; limit?: number; offset?: number; signal?: AbortSignal },
  ): Promise<EntitiesResponse> {
    const params = new URLSearchParams();
    if (options?.type) params.set('type', options.type);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.requestJson<EntitiesResponse>(
      `/document/${encodeURIComponent(documentId)}/nodes${qs ? `?${qs}` : ''}`,
      { method: 'GET', signal: options?.signal },
    );
  }

  // ─── Query (SQL) ─────────────────────────────────────────────────────────

  async query(documentId: string, sql: string, signal?: AbortSignal): Promise<QueryResult> {
    return this.requestJson<QueryResult>(
      `/document/${encodeURIComponent(documentId)}/query?select=${encodeURIComponent(sql)}`,
      { method: 'GET', signal },
    );
  }

  // ─── Logs ───────────────────────────────────────────────────────────────

  async logs(documentId: string, options?: LogsOptions): Promise<LogEntry[]> {
    const limit = options?.limit ?? 100;
    const res = await this.requestJson<{ entries: LogEntry[] }>(
      `/document/${encodeURIComponent(documentId)}/log?limit=${limit}`,
      { method: 'GET', signal: options?.signal },
    );
    return res.entries;
  }

  // ─── Stream (streaming completion via OpenAI SSE) ────────────────────────

  stream(
    documentId: string,
    query: string,
    options?: CompletionOptions,
  ): AsyncGenerator<CompletionEvent> {
    return createClientEventStream<CompletionEvent, ClientHttpError>(
      (emit) => this.streamEffect(documentId, query, options, emit),
      {
        signal: options?.signal,
        abortError: () =>
          new ClientHttpError({ method: 'completion_stream', cause: options?.signal?.reason }),
        restoreError: (error) => error.cause,
      },
    ).iterator;
  }

  /** Native completion producer; the public iterator supplies its bounded queue as the consumer. */
  streamEffect(
    documentId: string,
    query: string,
    options: CompletionOptions | undefined,
    emit: (event: CompletionEvent) => Effect.Effect<void, ClientHttpError>,
  ): Effect.Effect<void, ClientHttpError> {
    const client = this;
    return Effect.gen(function* () {
      const request = yield* Effect.try({
        try: () => ({
          path: `/document/${encodeURIComponent(documentId)}/chat/completions`,
          body: JSON.stringify({
            messages: [{ role: 'user', content: query }],
            stream: options?.stream !== false,
            ...(options?.model ? { model: options.model } : {}),
            ...(typeof options?.maxSteps === 'number' ? { max_steps: options.maxSteps } : {}),
          }),
        }),
        catch: (cause) => new ClientHttpError({ method: 'completion_stream', cause }),
      });
      return yield* client.withResponseEffect(
        request.path,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: request.body,
          signal: options?.signal,
        },
        Effect.fn('sdk.completionStream')(function* (response) {
          if (!response.ok) {
            const text = yield* readClientResponseTextEffect(response);
            return yield* new ClientHttpError({
              method: 'completion_stream',
              status: response.status,
              cause: new OkraRuntimeError(
                'HTTP_ERROR',
                `Completion failed: ${text}`,
                response.status,
              ),
            });
          }
          if (!response.body) {
            return yield* new ClientHttpError({
              method: 'completion_stream',
              cause: new OkraRuntimeError(
                'INVALID_RESPONSE',
                'No response body for completion stream',
                500,
              ),
            });
          }
          let fullText = '';
          yield* readClientResponseLinesEffect(
            response,
            (line) =>
              Effect.gen(function* () {
                const trimmed = line.trim();
                if (!trimmed || trimmed === 'data: [DONE]' || !trimmed.startsWith('data: ')) return;
                const events: CompletionEvent[] = [];
                try {
                  const chunk = JSON.parse(trimmed.slice(6)) as {
                    choices?: Array<{
                      delta?: { content?: string };
                      finish_reason?: string | null;
                    }>;
                    error?: { message?: string };
                  };
                  if (chunk.error)
                    events.push({
                      type: 'error',
                      message: chunk.error.message || 'Stream error',
                    });
                  else {
                    const delta = chunk.choices?.[0]?.delta?.content;
                    if (delta) {
                      fullText += delta;
                      events.push({ type: 'text_delta', text: delta });
                    }
                    if (chunk.choices?.[0]?.finish_reason === 'stop')
                      events.push({ type: 'done', answer: fullText });
                  }
                } catch {
                  return;
                }
                for (const event of events) yield* emit(event);
              }),
            false,
          );
        }),
      );
    });
  }

  // ─── Generate (non-streaming AI) ─────────────────────────────────────────

  async generate(
    documentId: string,
    query: string,
    options?: GenerateOptions & { schema?: undefined },
  ): Promise<GenerateResult>;
  async generate<T>(
    documentId: string,
    query: string,
    options: GenerateOptions & { schema: StructuredSchema<T> },
  ): Promise<GenerateResult<T>>;
  async generate<T = undefined>(
    documentId: string,
    query: string,
    options?: GenerateOptions,
  ): Promise<GenerateResult<T>> {
    return this.runHttpEffect(this.generateEffect<T>(documentId, query, options), options?.signal);
  }

  generateEffect<T = undefined>(
    documentId: string,
    query: string,
    options?: GenerateOptions,
  ): Effect.Effect<GenerateResult<T>, ClientHttpError> {
    if (options?.schema) return this.generateStructuredEffect<T>(documentId, query, options);
    const client = this;
    return Effect.gen(function* () {
      const request = yield* Effect.try({
        try: () => ({
          path: `/document/${encodeURIComponent(documentId)}/chat/completions`,
          body: JSON.stringify({
            messages: [{ role: 'user', content: query }],
            ...(options?.model ? { model: options.model } : {}),
            ...(typeof options?.maxSteps === 'number' ? { max_steps: options.maxSteps } : {}),
          }),
        }),
        catch: (cause) => new ClientHttpError({ method: 'generate', cause }),
      });
      const result = yield* client.requestJsonEffect<{
        id: string;
        choices: Array<{ message: { content: string } }>;
        usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number };
      }>(request.path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: request.body,
        signal: options?.signal,
      });

      return {
        answer: result.choices?.[0]?.message?.content || '',
      };
    });
  }

  private generateStructuredEffect<T>(
    documentId: string,
    query: string,
    options: GenerateOptions,
  ): Effect.Effect<GenerateResult<T>, ClientHttpError> {
    const client = this;
    const failure = (cause: unknown) =>
      new ClientHttpError({ method: 'generate_structured', cause });
    return Effect.gen(function* () {
      if (!query || query.trim() === '') {
        return yield* failure(
          new OkraRuntimeError(
            'INVALID_REQUEST',
            'generate with schema requires a non-empty query',
            400,
          ),
        );
      }

      const normalized = yield* normalizeStructuredSchemaEffect(
        options.schema as StructuredSchema<T>,
      ).pipe(Effect.mapError(failure));
      const path = yield* Effect.try({
        try: () => `/document/${encodeURIComponent(documentId)}/chat/completions`,
        catch: failure,
      });
      const init = yield* Effect.try({
        try: (): RequestInit => ({
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Opt-in source grounding (#505): server returns Anthropic-shaped
            // grounded citations in meta.citations when this is set.
            ...(options.cite ? { 'X-Okra-Cite': 'true' } : {}),
          },
          body: JSON.stringify({
            messages: [{ role: 'user', content: query }],
            response_format: {
              type: 'json_schema',
              json_schema: { name: 'result', schema: normalized.jsonSchema },
            },
            ...(options.model ? { model: options.model } : {}),
          }),
          signal: options.signal,
        }),
        catch: failure,
      });

      // Retry only retriable 504 timeouts, backing off 350ms per attempt
      // (350ms, 700ms) for up to maxAttempts total attempts.
      const maxAttempts = 3;
      const result = yield* client
        .requestJsonEffect<{
          id: string;
          choices: Array<{ message: { content: string } }>;
          usage?: {
            prompt_tokens?: number;
            completion_tokens?: number;
            total_tokens?: number;
          };
          meta?: StructuredOutputMeta;
        }>(path, init)
        .pipe(
          Effect.retry({
            while: ({ cause: error }) =>
              error instanceof OkraRuntimeError &&
              error.status === 504 &&
              ((typeof error.details === 'object' &&
                error.details !== null &&
                'timeoutMs' in error.details) ||
                error.message.toLowerCase().includes('timed out')),
            schedule: Schedule.recurs(maxAttempts - 1).pipe(
              Schedule.addDelay(({ attempt }) => Effect.succeed(Duration.millis(350 * attempt))),
            ),
          }),
        );

      const raw = result.choices?.[0]?.message?.content;
      if (!raw) {
        return yield* failure(
          new OkraRuntimeError('INVALID_RESPONSE', 'No content in structured output response', 500),
        );
      }

      const parsed: unknown = yield* Effect.try({
        try: () => JSON.parse(raw),
        catch: () =>
          failure(
            new OkraRuntimeError(
              'INVALID_RESPONSE',
              'Structured output response is not valid JSON',
              500,
            ),
          ),
      });

      let data: T;
      if (normalized.parser) {
        const zodResult = yield* Effect.try({
          try: () => normalized.parser!.safeParse(parsed),
          catch: failure,
        });
        if (!zodResult.success) {
          return yield* failure(
            new StructuredOutputError(
              'SCHEMA_VALIDATION_FAILED',
              'Client-side schema validation failed for structured output response',
              422,
              zodResult.error.issues,
            ),
          );
        }
        data = zodResult.data;
      } else {
        data = parsed as T;
      }

      return {
        answer: '',
        data,
        ...(result.meta ? { meta: result.meta } : {}),
      };
    });
  }

  // ─── List Documents ─────────────────────────────────────────────────────

  async listDocuments(signal?: AbortSignal): Promise<DocumentListItem[]> {
    const res = await this.requestJson<DocumentListResponse>('/v1/documents', {
      method: 'GET',
      signal,
    });
    return res.documents;
  }

  // ─── Delete Document ──────────────────────────────────────────────────

  async deleteDocument(documentId: string, signal?: AbortSignal): Promise<DeleteDocumentResult> {
    if (!documentId.trim()) {
      throw new OkraRuntimeError(
        'INVALID_REQUEST',
        'deleteDocument requires a non-empty documentId',
        400,
      );
    }
    await this.requestJson<{ ok: boolean }>(`/v1/documents/${encodeURIComponent(documentId)}`, {
      method: 'DELETE',
      signal,
    });
    return { deleted: true, documentId };
  }

  // ─── Read Document (full markdown) ────────────────────────────────────

  async read(documentId: string, options?: ReadDocumentOptions): Promise<ReadDocumentResult> {
    if (!documentId.trim()) {
      throw new OkraRuntimeError('INVALID_REQUEST', 'read requires a non-empty documentId', 400);
    }
    const params = options?.pages ? `?pages=${encodeURIComponent(options.pages)}` : '';
    return this.runHttpEffect(
      this.withResponseEffect(
        `/v1/documents/${encodeURIComponent(documentId)}/full.md${params}`,
        { method: 'GET', signal: options?.signal },
        Effect.fn('sdk.readDocument')(function* (response) {
          const markdown = yield* readClientResponseTextEffect(response);
          if (!response.ok) {
            return yield* new ClientHttpError({
              method: 'read_document',
              status: response.status,
              cause: new OkraRuntimeError(
                'HTTP_ERROR',
                `Read failed: ${markdown}`,
                response.status,
              ),
            });
          }
          return { documentId, markdown };
        }),
      ),
      options?.signal,
    );
  }

  // ─── Model Endpoint ──────────────────────────────────────────────────────

  modelEndpoint(documentId: string): string {
    return `${this.baseUrl}/document/${encodeURIComponent(documentId)}`;
  }

  // ─── Publish / Share ────────────────────────────────────────────────────

  async publish(documentId: string, signal?: AbortSignal): Promise<PublishResult> {
    const result = await this.requestJson<Omit<PublishResult, 'url'>>(
      `/document/${encodeURIComponent(documentId)}/publish`,
      { method: 'POST', signal },
    );
    return {
      ...result,
      url: `${this.baseUrl}/document/${encodeURIComponent(documentId)}`,
    };
  }

  async shareLink(documentId: string, options?: ShareLinkOptions): Promise<ShareLinkResult> {
    return this.requestJson<ShareLinkResult>(
      `/document/${encodeURIComponent(documentId)}/share-link`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: options?.role,
          label: options?.label,
          expiresInMs: options?.expiresInMs,
          maxViews: options?.maxViews,
        }),
        signal: options?.signal,
      },
    );
  }

  // ─── Workflows ───────────────────────────────────────────────────────────

  async runInvoiceExtraction(options: InvoiceExtractionRunOptions): Promise<InvoiceExtractionRun> {
    const result = await this.requestJson<InvoiceExtractionRunApiResponse>(
      '/v1/workflows/invoice-extraction/runs',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inputs: normalizeInvoiceWorkflowInputs(options.inputs),
          ...(options.tableId ? { table_id: options.tableId } : {}),
          ...(options.quality ? { quality: options.quality } : {}),
        }),
        signal: options.signal,
      },
    );

    return mapInvoiceExtractionRun(result);
  }

  async listInvoiceExtractionRuns(
    options: InvoiceExtractionRunListOptions = {},
  ): Promise<InvoiceExtractionRunList> {
    const params = new URLSearchParams();
    if (options.limit != null) params.set('limit', String(options.limit));
    if (options.cursor) params.set('cursor', options.cursor);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const result = await this.requestJson<{
      data: InvoiceExtractionRunApiResponse[];
      has_more: boolean;
      next_cursor: string | null;
    }>(`/v1/workflows/invoice-extraction/runs${suffix}`, {
      signal: options.signal,
    });

    return {
      data: result.data.map(mapInvoiceExtractionRun),
      hasMore: result.has_more,
      nextCursor: result.next_cursor,
    };
  }

  async getInvoiceExtractionRun(
    runId: string,
    signal?: AbortSignal,
  ): Promise<InvoiceExtractionRun> {
    const result = await this.requestJson<InvoiceExtractionRunApiResponse>(
      `/v1/workflows/invoice-extraction/runs/${encodeURIComponent(runId)}`,
      { signal },
    );
    return mapInvoiceExtractionRun(result);
  }

  async retryInvoiceExtractionRun(
    runId: string,
    signal?: AbortSignal,
  ): Promise<InvoiceExtractionRun> {
    const result = await this.requestJson<InvoiceExtractionRunApiResponse>(
      `/v1/workflows/invoice-extraction/runs/${encodeURIComponent(runId)}/retry`,
      { method: 'POST', signal },
    );
    return mapInvoiceExtractionRun(result);
  }

  async listInvoiceExtractionExceptions(
    runId: string,
    signal?: AbortSignal,
  ): Promise<InvoiceExtractionExceptionList> {
    const result = await this.requestJson<{
      data: InvoiceExtractionExceptionApiResponse[];
      has_more?: boolean;
      next_cursor?: string | null;
    }>(`/v1/workflows/invoice-extraction/runs/${encodeURIComponent(runId)}/exceptions`, { signal });

    return {
      data: result.data.map(mapInvoiceExtractionException),
      hasMore: result.has_more,
      nextCursor: result.next_cursor,
    };
  }

  async resolveInvoiceExtractionException(
    runId: string,
    exceptionId: string,
    options: InvoiceExtractionResolveExceptionOptions,
  ): Promise<InvoiceExtractionExceptionResolution> {
    const result = await this.requestJson<{
      id: string;
      run_id: string;
      table_id: string;
      row_id: string;
      status: 'resolved';
    }>(
      `/v1/workflows/invoice-extraction/runs/${encodeURIComponent(runId)}/exceptions/${encodeURIComponent(exceptionId)}/resolve`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          row_id: options.rowId,
          ...(options.field ? { field: options.field } : {}),
          ...(options.value !== undefined ? { value: options.value } : {}),
          ...(options.action ? { action: options.action } : {}),
        }),
        signal: options.signal,
      },
    );

    return {
      id: result.id,
      runId: result.run_id,
      tableId: result.table_id,
      rowId: result.row_id,
      status: result.status,
    };
  }

  // ─── Public HTTP ─────────────────────────────────────────────────────────

  request<T>(path: string, init: RequestInit = {}): Promise<T> {
    return this.runHttpEffect(this.requestEffect<T>(path, init));
  }

  /** Compose an authenticated JSON request without starting another Promise runtime. */
  requestEffect<T>(path: string, init: RequestInit = {}): Effect.Effect<T, ClientHttpError> {
    return withClientSignal(this.requestJsonEffect<T>(path, init), init.signal ?? undefined);
  }

  get url(): string {
    return this.baseUrl;
  }

  // ─── Internal HTTP ───────────────────────────────────────────────────────

  private authHeaders(): Record<string, string> {
    if (this.apiKey) return { Authorization: `Bearer ${this.apiKey}` };
    if (this.sharedSecret) return { 'x-document-agent-secret': this.sharedSecret };
    return {};
  }

  private async rawRequest(path: string, init: RequestInit): Promise<Response> {
    const headers = new Headers(init.headers);
    for (const [key, value] of Object.entries(this.authHeaders())) {
      if (!headers.has(key)) headers.set(key, value);
    }

    try {
      return await this.fetchImpl(`${this.baseUrl}${path}`, { ...init, headers });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new OkraRuntimeError('HTTP_ERROR', message, 502, {
        error: 'network_error',
        message,
        hint: 'Check the configured API base URL and network path, then run `okra doctor`.',
        retryable: true,
        next_actions: [
          { cmd: 'okra doctor', why: 'Diagnose auth, API reachability, and local CLI setup.' },
        ],
      });
    }
  }

  private async fetchAbsolutePromise(url: string, init: RequestInit): Promise<Response> {
    try {
      return await this.fetchImpl(url, init);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new OkraRuntimeError('HTTP_ERROR', message, 502, {
        error: 'network_error',
        message,
        hint: 'Check the network path and retry the request.',
        retryable: true,
        next_actions: [
          { cmd: 'okra doctor', why: 'Confirm the CLI can reach the configured API.' },
        ],
      });
    }
  }

  private requestJson<T>(path: string, init: RequestInit): Promise<T> {
    return this.runHttpEffect(this.requestJsonEffect<T>(path, init), init.signal ?? undefined);
  }

  private runHttpEffect<A>(
    program: Effect.Effect<A, ClientHttpError>,
    signal?: AbortSignal,
  ): Promise<A> {
    return Effect.runPromise(program.pipe(Effect.catch((error) => Effect.fail(error.cause))), {
      signal,
    });
  }

  private withResponseEffect<A>(
    path: string,
    init: RequestInit,
    use: (response: Response) => Effect.Effect<A, ClientHttpError>,
  ): Effect.Effect<A, ClientHttpError> {
    return withFetchedResponseEffect(init, (ownedInit) => this.rawRequest(path, ownedInit), use);
  }

  private requestJsonEffect<T>(path: string, init: RequestInit): Effect.Effect<T, ClientHttpError> {
    const client = this;
    return this.withResponseEffect(
      path,
      init,
      Effect.fn('sdk.requestJson')(function* (response) {
        const text = yield* readClientResponseTextEffect(response);
        return yield* Effect.try({
          try: () => client.projectJsonResponse<T>(path, response, text),
          catch: (cause) =>
            new ClientHttpError({ method: 'request_json', status: response.status, cause }),
        });
      }),
    );
  }

  private projectJsonResponse<T>(path: string, response: Response, text: string): T {
    const parsed = this.parseBody(text);

    if (!response.ok) {
      const envelope = parsed as StructuredErrorEnvelope | null;
      const nestedError =
        typeof envelope?.error === 'object' && envelope.error !== null ? envelope.error : undefined;
      const code = envelope?.code || nestedError?.code;
      const message =
        getErrorMessage(envelope?.message) ||
        getErrorMessage(typeof envelope?.error === 'string' ? envelope.error : undefined) ||
        getErrorMessage(nestedError?.message) ||
        `Request failed with status ${response.status}`;
      const details = envelope?.details ?? nestedError?.details ?? parsed ?? text;
      if (isStructuredCode(code)) {
        throw new StructuredOutputError(code, message, response.status, details);
      }
      const runtimeCode: RuntimeErrorCode = response.status === 401 ? 'UNAUTHORIZED' : 'HTTP_ERROR';
      throw new OkraRuntimeError(runtimeCode, message, response.status, details);
    }

    if (parsed === null) {
      throw new OkraRuntimeError(
        'INVALID_RESPONSE',
        `Expected JSON response for ${path}`,
        response.status,
        text,
      );
    }

    return parsed as T;
  }

  private parseBody(text: string): unknown | null {
    const trimmed = text.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  private resolveBinaryUploadEffect = Effect.fn('sdk.resolveBinaryUpload')(function* (
    input: UploadInput,
    preferredFileName?: string,
  ) {
    let bytes: Uint8Array;
    let fileName = preferredFileName || 'document.pdf';

    if (typeof input === 'string') {
      const local = yield* readLocalFileEffect(input);
      bytes = local.bytes;
      if (!preferredFileName) fileName = local.fileName;
    } else if (isBlobLike(input)) {
      const buffer = yield* Effect.tryPromise({
        try: () => input.arrayBuffer(),
        catch: (cause) => new ClientHttpError({ method: 'read_blob', cause }),
      });
      bytes = toUint8Array(buffer);
      if (!preferredFileName) {
        fileName = inferBlobName(input, fileName);
      }
    } else {
      bytes = toUint8Array(input);
    }

    return { bytes, fileName };
  });
}
